// Bottom-docked bundle widget for collection pages. Reads the in-progress bundle from
// sessionStorage (drool_bundle_v1 — same key the bundle builder persists). Collapsed = a
// full-width yellow bar with a dynamic "you're X away" message; expanded = a white panel
// with the bundle thumbnails, the running Total (struck + discounted + SAVE pill) and a
// black pill CTA. (Figma 115-2087 open / 115-1940 closed.)
const KEY = 'drool_bundle_v1';
const COLLAPSE_KEY = 'drool_bw_collapsed';

// Savings tooltip — SAME copy as the PDP "SAVE X" tooltip (feedback 6).
// Savings now also absorb any sale markdown, so the tooltip has to name all three sources.
// "any sale reduction" keeps it accurate when nothing is marked down.
const SAVE_TOOLTIP = 'Savings are based on any sale reduction, any pack discount and the value of free medium print(s).';
const SAVE_ICON = '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.3"/><path d="M8 7v4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><circle cx="8" cy="4.7" r="0.9" fill="currentColor"/></svg>';

// Bundle tiers. The real values come from the shared Theme Setting "Bundle Builder Offer",
// injected on <bundle-widget data-tier-config> by the snippet (single source, market-resolved).
// These are only a fallback if that attribute is missing/unparseable.
const DEFAULT_TIERS = { duo: { paid: 2, free: 2, disc: 20 }, quad: { paid: 3, free: 3, disc: 22 }, gallery: { paid: 4, free: 4, disc: 24 }, mega: { paid: 5, free: 5, disc: 26 }, pack6: { paid: 6, free: 6, disc: 28 }, pack7: { paid: 7, free: 7, disc: 30 }, pack8: { paid: 8, free: 8, disc: 32 }, pack9: { paid: 9, free: 9, disc: 34 }, pack10: { paid: 10, free: 10, disc: 36 } };

// `versioned` = the snippet stamped data-tier-config-v, so the config lists exactly the packs on
// offer and any absent pack was switched OFF by the merchant — never backfill those. Without the
// stamp we are reading a cached pre-ladder snippet (duo/quad/gallery only), where a missing pack
// means "shipped later", so the defaults fill the gap instead of truncating the ladder.
function parseTiers(str, versioned) {
  try {
    const c = JSON.parse(str);
    if (c && typeof c === 'object' && Object.keys(c).length) {
      if (!versioned) Object.keys(DEFAULT_TIERS).forEach((k) => { c[k] = c[k] || Object.assign({}, DEFAULT_TIERS[k]); });
      // Paid counts are structural, not merchant-editable — pin them on whatever survived.
      Object.keys(c).forEach((k) => { if (DEFAULT_TIERS[k]) c[k].paid = DEFAULT_TIERS[k].paid; });
      return c;
    }
  } catch (e) {}
  return DEFAULT_TIERS;
}
// The pack ladder, smallest first — read straight off the config, so a pack added to the shared
// Theme setting flows through here without another edit.
function tierSteps(tiers) {
  return Object.keys(tiers)
    .map((k) => ({ key: k, count: Number(tiers[k].paid) || 0, discount: Number(tiers[k].disc) || 0, free: Number(tiers[k].free) || 0 }))
    .filter((t) => t.count > 0)
    .sort((a, b) => a.count - b.count);
}

function loadBundle() {
  try {
    const d = JSON.parse(sessionStorage.getItem(KEY) || 'null');
    return d && d.ts ? d : null;
  } catch (e) {
    return null;
  }
}

function money(cents) {
  const code = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: code, minimumFractionDigits: (cents % 100 ? 2 : 2) }).format(cents / 100);
  } catch (e) {
    return '£' + (cents / 100).toFixed(2);
  }
}

function variantOf(product, size, frame) {
  if (!product || !product.variants || !product.variants.length) return null;
  return product.variants.find((x) => x.option1 === size && x.option2 === frame)
    || product.variants.find((x) => x.option1 === size)
    || product.variants[0];
}

function variantPrice(product, size, frame) {
  const v = variantOf(product, size, frame);
  return v ? v.price : 0;
}

// ---- Frame compositing (same source + rules as the cart rows & bundle builder) ----
// The cart always renders a print's SECOND image (the raw art, item.product.images[1]) and,
// when the line carries `_frame_id`, overlays that exact frame's PNG from the `frames`
// metaobject (cart-products.liquid). The widget replicates that: frames come from the global
// #mrk-frames-data template (theme.liquid), the art from the saved slot's artImage (the
// builder already stores images[1]) or a cached /products/{handle}.js lookup for cart lines.
let framesCache = null;
function framesList() {
  if (framesCache) return framesCache;
  const tpl = document.getElementById('mrk-frames-data');
  const imgs = tpl && tpl.content ? [...tpl.content.querySelectorAll('img.mrk-frame-image-item')] : [];
  framesCache = imgs.map((img) => ({
    frameId: img.getAttribute('frame-id'),
    color: (img.getAttribute('color') || '').toLowerCase(),
    alt: (img.getAttribute('alt-name') || '').toLowerCase(),
    type: img.getAttribute('type') || '',
    ratio: parseFloat(img.getAttribute('ratio')) || 0,
    lr: parseFloat(img.getAttribute('left_right')) || 0,
    tb: parseFloat(img.getAttribute('top_bottom')) || 0,
    image: img.getAttribute('src') || '',
  }));
  return framesCache;
}

// The frame to composite for a print: the EXACT `_frame_id` the cart line carries wins;
// otherwise (session slots / framed-print-only lines) the PDP's colour + type + ratio (±0.02)
// match — identical to bundle-builder's _frameOverlay. null = unframed / no match.
function frameOverlay(p) {
  const frames = framesList();
  if (!p || !frames.length) return null;
  if (p.frameId) {
    const exact = frames.find((f) => String(f.frameId) === String(p.frameId));
    if (exact) return exact;
  }
  if (!p.frame || /unframed|no frame/i.test(p.frame)) return null;
  const word = String(p.frame).trim().split(/\s+/).pop().toLowerCase(); // black / white / natural
  const ratio = p.ratio || 0.707;
  let cands = frames.filter((f) => f.color === word || f.alt === word);
  if (p.type) cands = cands.filter((f) => f.type === p.type);
  cands = cands.filter((f) => Math.abs((f.ratio || 0) - ratio) <= 0.02); // PDP tolerance
  if (!cands.length) return null;
  cands.sort((a, b) => Math.abs((a.ratio || 0) - ratio) - Math.abs((b.ratio || 0) - ratio));
  return cands[0];
}

// Raw-art lookup for cart lines — /cart.js only exposes the line's FIRST (framed-mockup)
// image, so fetch the product once per handle and take images[1] (the same positional
// convention the cart renders). While in flight / on failure callers keep the line image;
// onLoad fires once when the art arrives so the widget can re-render.
const artCache = {};
function cartArt(handle, onLoad) {
  if (!handle) return null;
  if (handle in artCache) return artCache[handle];
  artCache[handle] = null; // request in flight
  fetch('/products/' + handle + '.js')
    .then((r) => (r.ok ? r.json() : null))
    .then((p) => {
      if (!p) return;
      const imgs = p.images || [];
      const m = (p.media || [])[1] || (p.media || [])[0];
      let ratio = 0;
      if (m) {
        const w = m.width || (m.preview_image && m.preview_image.width);
        const h = m.height || (m.preview_image && m.preview_image.height);
        if (w && h) ratio = w / h;
      }
      // Variant compare-at prices — the drawer's Subtotal/SAVE include this "Sale" markup
      // (cart-summary.liquid) but /cart.js doesn't expose compare-at, so keep it from the
      // same product fetch the thumbnails already need (no extra request).
      const cmpMap = {};
      (p.variants || []).forEach((v) => { if (v.compare_at_price) cmpMap[v.id] = v.compare_at_price; });
      artCache[handle] = {
        artImage: imgs[1] || imgs.find((u) => /mock-up/i.test(u)) || imgs[0] || null,
        ratio: ratio || 0.707,
        type: p.type || '',
        compareAt: cmpMap,
      };
      if (onLoad) onLoad();
    })
    .catch(() => { /* keep the line-image fallback */ });
  return null;
}

// Pre-sale price of the SAME variant variantPrice() picks. `compare_at_price` is null across
// most of the catalogue and is sometimes stored equal to `price`, so only a value strictly
// above the price counts as a markdown — otherwise the was-price IS the price and the widget
// renders exactly as it did before.
function variantWasPrice(product, size, frame) {
  const v = variantOf(product, size, frame);
  if (!v) return 0;
  const p = v.price || 0;
  const c = v.compare_at_price || 0;
  return c > p ? c : p;
}

function paidSlots(data) {
  if (!data) return [];
  const slots = (data.paidSlots || []).filter((s) => s && s.assigned && s.product);
  const overflow = (data.overflow || []).filter((o) => o && o.product).map((o) => ({ assigned: true, product: o.product, size: o.size, frame: o.frame }));
  return slots.concat(overflow);
}
function freeSlots(data) {
  if (!data) return [];
  return (data.freeSlots || []).filter((s) => s && s.assigned && s.product);
}

function nextTier(n, steps) {
  for (const t of steps) if (n < t.count) return { ...t, away: t.count - n };
  return null;
}

// Free prints earned by CART VALUE instead of pack tiers (theme settings > Cart Drawer > Free prints
// trigger), published for this visitor's market by snippets/wd-free-trigger.liquid. null = pack
// tiers, and the widget behaves exactly as it always has. Read once: it cannot change without a load.
const FREE_TRIGGER = (() => {
  try {
    const el = document.getElementById('wd-free-trigger');
    const c = el && JSON.parse(el.textContent);
    return c && c.mode === 'price' && Number(c.threshold) > 0 && Number(c.per) > 0
      ? { threshold: Number(c.threshold), per: Number(c.per) } : null;
  } catch (e) { return null; }
})();

// Cart-value version of the bar: the pack discount still moves with the number of prints, the free
// prints with the money (each full threshold earns `per`, repeating). Mirrors bundle-builder.js
// _valueProgressMessage() — keep the two in step.
function valueMessage(n, steps, value, freeTaken, ft) {
  const top = steps[steps.length - 1];
  let pack = '';
  if (top && top.discount > 0) {
    const next = steps.find((s) => n < s.count);
    const current = steps.slice().reverse().find((s) => n >= s.count && s.discount > 0);
    if (n === 0) pack = `Save up to ${top.discount}%`;
    else if (!next) pack = `You’ve unlocked ${top.discount}% off`;
    else if (current) pack = `You’ve unlocked ${current.discount}% off`;
    else if (next.discount > 0) pack = `You’re ${next.count - n} ${next.count - n === 1 ? 'print' : 'prints'} away from ${next.discount}% off`;
  }
  const c = Math.max(0, value);
  const left = Math.floor(c / ft.threshold) * ft.per - freeTaken;
  let free;
  if (left > 0) free = `${left} free print${left > 1 ? 's' : ''} unlocked`;
  else if (c === 0) free = `spend ${money(ft.threshold)} for ${ft.per} free prints`;
  else free = `spend ${money((Math.floor(c / ft.threshold) + 1) * ft.threshold - c)} more for ${ft.per} free prints`;
  return pack ? `${pack} · ${free}` : free.charAt(0).toUpperCase() + free.slice(1);
}

function message(n, steps) {
  const top = steps[steps.length - 1]; // best tier (maxed copy)
  // Zero-discount offer (sale mode, tier discount settings all 0): every state below
  // references only the free prints — never "0% off".
  const zero = (s) => (Number(s && s.discount) || 0) === 0;
  // (a) empty — quoted off the smallest pack still enabled, not a hardcoded 2-pack.
  const entry = steps[0];
  if (n === 0) {
    if (zero(entry)) return `You’re ${entry.count} ${entry.count === 1 ? 'print' : 'prints'} away from ${entry.free} free prints`;
    return `You’re ${entry.count} ${entry.count === 1 ? 'print' : 'prints'} away from ${entry.discount}% off and ${entry.free} free prints`;
  }
  const t = nextTier(n, steps);        // next tier not yet reached (null once maxed)
  // (d) maxed out
  if (!t) {
    if (zero(top)) return `Nice! You’ve unlocked ${top.free} free prints`;
    return `Nice! You’ve unlocked ${top.discount}% off + ${top.free} free prints`;
  }
  // (c) has already unlocked a discount (reached at least one tier) but isn't maxed
  const current = steps.slice().reverse().find((s) => n >= s.count) || null;
  if (current) {
    if (zero(current)) return `You’ve unlocked ${current.free} free print${current.free > 1 ? 's' : ''}`;
    return `You’ve unlocked ${current.discount}% off and ${current.free} free print${current.free > 1 ? 's' : ''}`;
  }
  // (b) not yet unlocked — distance to the first tier
  const noun = t.away === 1 ? 'print' : 'prints';
  if (zero(t) && t.free > 0) return `You’re ${t.away} ${noun} away from ${t.free} free print${t.free > 1 ? 's' : ''}`;
  let msg = `You’re ${t.away} ${noun} away from ${t.discount}% off`;
  if (t.free > 0) msg += ` and ${t.free} free print${t.free > 1 ? 's' : ''}`;
  return msg;
}

function thumb(print, isFree, empty) {
  const inCart = !!(print && print._inCart);
  const el = document.createElement('div');
  el.className = 'bundle-widget__item' + (isFree ? ' bundle-widget__item--free' : '') + (empty ? ' bundle-widget__item--empty' : '') + (inCart ? ' bundle-widget__item--incart' : '');
  if (empty) {
    el.innerHTML = '<span class="bundle-widget__item-plus">+</span>';
    return el;
  }
  let badgeHost = el; // framed thumbs re-point this at the art wrapper below
  // Composite the frame ONLY once the raw art is known (print.art) — framing the cart line's
  // first image would draw a frame around an already-framed mockup. Until the art fetch lands
  // the thumb shows the line image exactly as before.
  const overlay = print && print.art ? frameOverlay(print) : null;
  const src = print && (print.art || print.image);
  if (src && overlay) {
    el.classList.add('wd-bw-framed');
    const box = document.createElement('div');
    box.className = 'wd-bw-frame-box';
    box.style.setProperty('--tb', (overlay.tb || 0) / 2 + '%');
    box.style.setProperty('--lr', (overlay.lr || 0) / 2 + '%');
    const fr = document.createElement('img');
    fr.className = 'wd-bw-frame-overlay';
    fr.src = overlay.image;
    fr.alt = '';
    fr.loading = 'lazy';
    const media = document.createElement('div');
    media.className = 'wd-bw-frame-media';
    // The art gets its own relative wrapper so the "In cart" badge can anchor to the
    // ART's bottom edge (inside the frame window) — the tile itself can be taller than
    // the composite (flex stretch / min-height), so the tile bottom is the wrong anchor.
    const artbox = document.createElement('div');
    artbox.className = 'wd-bw-frame-artbox';
    const art = document.createElement('img');
    art.className = 'wd-bw-frame-art';
    art.src = src;
    art.alt = print.title || '';
    art.loading = 'lazy';
    artbox.appendChild(art);
    media.appendChild(artbox);
    box.appendChild(fr);
    box.appendChild(media);
    el.appendChild(box);
    badgeHost = artbox;
  } else if (src) {
    const img = document.createElement('img');
    img.src = src;
    img.alt = print.title || '';
    img.loading = 'lazy';
    // Same ratio convention as the cart rows (--ratio = images[1].aspect_ratio): size the
    // tile to the art's own aspect so landscape prints aren't cover-cropped into the fixed
    // portrait box. Unknown ratio (art fetch still in flight) keeps the default box.
    if (print.ratio > 0) {
      el.classList.add('wd-bw-ratio');
      el.style.setProperty('--wd-ratio', print.ratio);
      // Same structure as framed tiles: art in a relative wrapper with the badge anchored
      // to IT, in-flow height — the previous percentage-height/aspect-ratio maths resolved
      // differently on iOS WebKit and let art peek out below the badge.
      const artbox = document.createElement('div');
      artbox.className = 'wd-bw-frame-artbox';
      artbox.appendChild(img);
      el.appendChild(artbox);
      badgeHost = artbox;
    } else {
      el.appendChild(img);
    }
  }
  // "In cart" badge (feedback) — same treatment the removed "FREE" tag used on the thumbnail.
  // Appended to badgeHost so on framed thumbs it anchors to the art, not the tile.
  if (inCart) {
    const badge = document.createElement('span');
    badge.className = 'bundle-widget__item-incart';
    badge.textContent = 'In cart';
    badgeHost.appendChild(badge);
  }
  return el;
}

// In-progress prints from the saved bundle, normalised to {image, art, title, price} plus the
// slot's frame/ratio/type so the thumb can composite the chosen frame. artImage IS the second
// (raw art) product image — the builder stores it with that positional convention.
function sessionPrints(data) {
  const map = (s) => ({
    image: (s.product && (s.product.image || s.product.artImage)) || null,
    art: (s.product && (s.product.artImage || s.product.image)) || null,
    ratio: (s.product && s.product.ratio) || null,
    type: (s.product && s.product.type) || '',
    frame: s.frame || null,
    svc: !!(s.product && s.product.framingService), // print carries the framing-service tag
    title: s.product && s.product.title,
    price: variantPrice(s.product, s.size, s.frame),
    was: variantWasPrice(s.product, s.size, s.frame),
  });
  // removeFraming = the builder's "Remove framing service" toggle, persisted with the bundle.
  return { paid: paidSlots(data).map(map), free: freeSlots(data).map(map), tier: data && data.tier, removeFraming: !!(data && data.removeFraming) };
}

// Pre-sale price for a cart line. cart.js exposes no compare_at_price, so it comes from the
// product endpoint — one in-flight request per distinct handle, cached for the page, so a cart of
// several prints costs a handful of small requests rather than one per line. Any failure falls
// back to the price actually charged: understating a saving is safe, overstating one is not.
const wasCache = new Map();
async function wasPriceForLine(line) {
  const price = line.price || 0;
  if (!line.handle || !line.variantId) return price;
  if (!wasCache.has(line.handle)) {
    wasCache.set(line.handle, fetch('/products/' + line.handle + '.js')
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null));
  }
  const p = await wasCache.get(line.handle);
  const v = p && (p.variants || []).find((x) => String(x.id) === String(line.variantId));
  const cap = (v && v.compare_at_price) || 0;
  return cap > price ? cap : price;
}

// ALL prints in the CART count toward the pack — not just ones added via the bundle (item GA).
// A print added via the normal PDP or the PLP Quick-Add has no `_bundle_type`, but it's still a
// print in the cart, so it must appear here. We include every "Print"-type line, and exclude the
// physical frame + framing-service child lines (and any £0 non-bundle gift/upsell line).
async function cartPrints() {
  try {
    const cart = await fetch('/cart.js', { cache: 'no-store' }).then((r) => r.json());
    const paid = [], free = [];
    let tier = null;
    (cart.items || []).forEach((i) => {
      const pr = i.properties || {};
      if (pr._bundle_type) tier = tier || pr._bundle_type;
      const type = (i.product_type || '').toLowerCase();
      const title = i.product_title || i.title || '';
      const isPrint = type.indexOf('print') > -1; // catalogue prints are type "Print"
      if (!isPrint) return;                         // skip frames, framing service, hooks, etc.
      if (/framing service/i.test(title)) return;
      const price = i.original_price || i.price || 0;
      // Frame identity for the thumb composite: the exact `_frame_id` the line carries
      // (child-frame flow), plus the frame option value as a fallback for framed-print-only
      // lines without one — same parse as bundle-builder's _parseCartVariant.
      let frame = null;
      (i.options_with_values || []).forEach((ov) => {
        const n = (ov.name || '').toLowerCase();
        if (n.indexOf('fram') > -1) frame = ov.value;
      });
      if (!frame && i.variant_title) frame = i.variant_title.split(' / ')[1] || null;
      // `was` is filled in below — cart.js has no compare_at_price and its `original_price` is
      // already the marked-down price, so the pre-sale figure has to be fetched.
      const obj = { image: i.image || i.featured_image || null, title: i.product_title || i.title || '', price, was: price, _inCart: true, handle: i.handle || null, frameId: pr._frame_id || null, frame, type: i.product_type || '', variantId: i.variant_id || i.id, qty: i.quantity || 1 };
      // Free is whatever the discount actually zeroed, in both modes — the same rule as the cart
      // drawer (cart-drawer.liquid counts final_line_price == 0). A print given free from the cart
      // recommendations or the quick add carries no _bundle_free, and _bundle_free is stamped once
      // at add time and never cleared: after the pack changes (a paid print removed) a line can keep
      // it while being charged in full. Trusting the property put that paid print in a free slot,
      // so two paid prints read as one ("1 print away from ... 2 free prints").
      if (i.final_line_price === 0 && price > 0) free.push(obj);
      else if (price > 0) paid.push(obj);           // ignore £0 non-bundle gift lines
    });
    // Recover each line's pre-sale price so the struck total and SAVE stay correct once a print
    // moves from the in-progress bundle into the cart. Without this the widget would silently
    // under-report the saving at exactly the point the shopper commits.
    await Promise.all([...paid, ...free].map(async (o) => { o.was = await wasPriceForLine(o); }));
    // Real cart money (feedback): original vs discounted totals straight from Shopify —
    // they reflect every automatic discount that ACTUALLY applied (and every line it
    // applied to, framing services included), exactly what the cart drawer shows.
    const totals = { original: cart.original_total_price || 0, total: cart.total_price || 0 };
    return { paid, free, tier, totals };
  } catch (e) { return { paid: [], free: [], tier: null, totals: null }; }
}

// Union of two print lists, de-duped by title+price so cart + in-progress don't double up.
function mergePrints(a, b) {
  const seen = new Set(), out = [];
  [...(a || []), ...(b || [])].forEach((p) => { const k = (p.title || '') + '|' + (p.price || 0); if (!seen.has(k)) { seen.add(k); out.push(p); } });
  return out;
}

class BundleWidget extends HTMLElement {
  connectedCallback() {
    // Sale A/B test: variant arm (html.bb-off-plp — set by Visually / the theme's
    // wd-sale-ab-prepaint snippet) has no PLP widget. Stay hidden and wire nothing —
    // the snippet's CSS rule hides it as well (belt and braces).
    if (document.documentElement.classList.contains('bb-off-plp')) return;
    this.tiers = parseTiers(this.dataset.tierConfig, this.dataset.tierConfigV === '2'); // shared offer config (item: single source)
    this.tierSteps = tierSteps(this.tiers);
    this.panel = this.querySelector('[data-bw-panel]');
    this.bar = this.querySelector('[data-bw-toggle]');
    this.barText = this.querySelector('[data-bw-bartext]');
    this.basePrice = Number(this.dataset.basePrice) || 2800; // base print price (cents) for the "from" teaser
    this.framingFee = Number(this.dataset.framingFee) || 0;  // framing-service fee (cents, market-converted by the snippet)
    // Its pre-sale price, for the struck total only. Falls back to the fee when it isn't marked down.
    this.framingFeeWas = Math.max(this.framingFee, Number(this.dataset.framingFeeWas) || 0);
    this.itemsEl = this.querySelector('[data-bw-items]');
    this.saveEl = this.querySelector('[data-bw-save]');
    this.totalEl = this.querySelector('[data-bw-total]');
    this.totalLabelEl = this.querySelector('.bundle-widget__total-label');
    this.closeBtn = this.querySelector('[data-bw-close]');
    this.cta = this.querySelector('[data-bw-cta]');
    this.viewEl = this.querySelector('[data-bw-view]');

    this.bar.addEventListener('click', () => this.toggle());
    if (this.closeBtn) this.closeBtn.addEventListener('click', (e) => { e.stopPropagation(); this.setOpen(false, true); });

    this.render();
    this.removeAttribute('hidden');

    window.addEventListener('pageshow', () => this.render());
    window.addEventListener('storage', (e) => { if (e.key === KEY) this.render(); });
    // Reflect cart adds/removes (item F4). The theme fires BOTH 'cart:update' (CartUpdateEvent) and
    // 'cart:updated' depending on the path — desktop removal fired one, the mobile cart the other, so
    // the widget went stale on mobile (feedback). Listen to both. Also re-render when the tab regains
    // focus / becomes visible, in case a removal happened on a separate /cart page.
    document.addEventListener('cart:update', () => this.render());
    document.addEventListener('cart:updated', () => this.render());
    window.addEventListener('focus', () => this.render());
    document.addEventListener('visibilitychange', () => { if (!document.hidden) this.render(); });
  }

  async render() {
    const sess = sessionPrints(loadBundle());
    const cart = await cartPrints();                 // include what's already in the cart (item F4)
    let paid = mergePrints(cart.paid, sess.paid);
    let free = mergePrints(cart.free, sess.free);

    // Resolve the raw art (second product image) for cart lines — /cart.js only carries the
    // first image. Cached once per handle; the widget re-renders when each fetch lands.
    [...paid, ...free].forEach((p) => {
      if (p.art || !p.handle) return;
      const a = cartArt(p.handle, () => this.render());
      if (a) {
        p.art = a.artImage || p.image;
        if (!p.ratio) p.ratio = a.ratio;
        if (!p.type) p.type = a.type;
      }
    });

    const n = paid.length;
    const hasBundle = n > 0 || free.length > 0;

    if (FREE_TRIGGER) {
      // What the cart will be worth: Shopify's own total (after every discount, free prints at £0)
      // plus the paid prints still being chosen in the builder, at the pack discount they reach.
      const key = (p) => (p.title || '') + '|' + (p.price || 0);
      const inCart = new Set((cart.paid || []).map(key));
      const pending = (sess.paid || []).filter((p) => !inCart.has(key(p))).reduce((sum, p) => sum + (Number(p.price) || 0), 0);
      const reachedNow = this.tierSteps.filter((st) => n >= st.count).pop();
      const pct = reachedNow ? reachedNow.discount : 0;
      const value = Math.round(((cart.totals && cart.totals.total) || 0) + pending * (1 - pct / 100));
      this.barText.textContent = valueMessage(n, this.tierSteps, value, free.length, FREE_TRIGGER);
    } else {
      this.barText.textContent = message(n, this.tierSteps);
    }

    // Tier config: prefer the cart/saved tier, else the biggest pack the paid count has reached
    // (tierSteps is sorted smallest-first, so the last match wins). Below the entry pack, quote it.
    const reached = this.tierSteps.filter((s) => n >= s.count).pop();
    const tier = this.tiers[cart.tier || sess.tier] || (reached && this.tiers[reached.key]) || this.tiers[this.tierSteps[0].key];

    // Any print beyond the tier's PAID quota fills a FREE slot — so a 3rd print lands in the
    // "free print" slot rather than an extra paid slot, however it was added (feedback 2).
    if (paid.length > tier.paid) {
      free = paid.slice(tier.paid).concat(free);
      paid = paid.slice(0, tier.paid);
    }

    // Thumbnails: EVERY print the shopper has (paid + free) always renders, plus dashed empties
    // up to the tier capacity. Use max() so a print is never dropped even if it exceeds the
    // (possibly stale) hardcoded tier count — this was hiding the free prints (item F4).
    this.itemsEl.innerHTML = '';
    const paidSlotsN = Math.max(tier.paid, paid.length);
    for (let i = 0; i < paidSlotsN; i++) {
      this.itemsEl.appendChild(paid[i] ? thumb(paid[i], false, false) : thumb(null, false, true));
    }
    const freeSlotsN = Math.max(tier.free, free.length);
    for (let i = 0; i < freeSlotsN; i++) {
      this.itemsEl.appendChild(free[i] ? thumb(free[i], true, false) : thumb(null, true, true));
    }

    // Running total: paid prints discounted; free prints struck-through value on top.
    const paidOriginal = paid.reduce((sum, p) => sum + (p.price || 0), 0);
    const freeValue = free.reduce((sum, p) => sum + (p.price || 0), 0);
    let sale = Math.round(paidOriginal * (1 - tier.disc / 100));
    // Struck at PRE-SALE prices so a flash sale grows the SAVE pill instead of shrinking it,
    // matching the PDP builder. Falls back to `price` per print, so a bundle with nothing
    // marked down produces the same struck total as before.
    let struck = paid.reduce((sum, p) => sum + (p.was || p.price || 0), 0)
      + free.reduce((sum, p) => sum + (p.was || p.price || 0), 0);
    let fromPrefix = '';
    // Framing service (parity with the builder's ADD TO CART total): flat fee × the framed,
    // service-eligible prints not yet in the cart, zero while the bundle's "Remove framing
    // service" toggle is on. Added to BOTH totals undiscounted — the pack % never applies to
    // the service, same as bundle-builder.js (framingAdd). In-cart prints are excluded: their
    // real service lines are already inside the cart-truth totals below.
    const framingCount = sess.removeFraming ? 0 : paid.concat(free).filter((p) =>
      !p._inCart && p.svc && p.frame && !/unframed/i.test(p.frame)).length;
    const framingAdd = this.framingFee * framingCount;
    sale += framingAdd;
    struck += this.framingFeeWas * framingCount;
    // CART TRUTH (feedback): once every displayed print is actually in the cart, show the
    // cart's REAL money — whatever discount tier Shopify genuinely applied — so the widget
    // Total always equals the drawer Total. The projected pack-tier maths above would
    // over-promise (e.g. 24% for 4 paid prints when only "Buy 2 Get 20%" is live). The
    // projection remains only while there are in-progress prints not yet in the cart.
    const shown = paid.concat(free);
    if (cart.totals && shown.length && shown.every((p) => p._inCart)) {
      // Compare-at ("Sale") markup: the drawer's Subtotal is original_total_price PLUS
      // Σ (compare_at − original_price) × qty (cart-summary.liquid), shown there as its own
      // "Sale −£X" row. PRIMARY source: the drawer's own Liquid-computed cents value,
      // stamped on .cart__summary-totals as data-wd-sale-total (market-aware — Liquid sees
      // the session's price-list compare-ats, which the AJAX product endpoints do NOT
      // expose — and morph-refreshed on every cart change). The product.js computation
      // stays only as a fallback for when that node is absent.
      let saleMarkup = 0;
      const saleNode = document.querySelector('[data-wd-sale-total]');
      if (saleNode) {
        saleMarkup = parseInt(saleNode.getAttribute('data-wd-sale-total'), 10) || 0;
      } else {
        shown.forEach((p) => {
          const known = p.handle && artCache[p.handle];
          const cmp = known && known.compareAt && known.compareAt[p.variantId];
          if (cmp && cmp > (p.price || 0)) saleMarkup += (cmp - (p.price || 0)) * (p.qty || 1);
        });
      }
      struck = cart.totals.original + saleMarkup;
      sale = cart.totals.total;
    }
    if (!hasBundle) {
      // Empty state (item G4): a "From <s>£56</s> £44.80" teaser = the entry pack at the
      // base print price. data-base-price is market-true (feedback): the snippet resolves it
      // from the collection's first print (price_min, price-list converted by Liquid) because
      // raw setting numbers are never market-converted; the bundle_base_price setting remains
      // only as the fallback when the collection has no priced print.
      const entryPack = this.tiers[this.tierSteps[0].key];
      struck = entryPack.paid * this.basePrice;
      sale = Math.round(struck * (1 - entryPack.disc / 100));
      fromPrefix = '<span class="bundle-widget__from">From</span> ';
    }
    const save = struck - sale;
    if (this.totalEl) {
      this.totalEl.innerHTML = fromPrefix + (struck > sale ? '<s>' + money(struck) + '</s> ' : '') + money(sale);
    }
    if (this.totalLabelEl) this.totalLabelEl.style.display = hasBundle ? '' : 'none'; // "Total" hidden on the "From" teaser
    this.dataset.state = hasBundle ? 'total' : 'from'; // drives the mobile CTA layout (feedback): From = beside price, Total = own row
    if (this.saveEl) {
      if (!hasBundle || save <= 0) {
        this.saveEl.innerHTML = '';
        this.saveEl.style.display = 'none';
      } else {
        this.saveEl.innerHTML = 'SAVE ' + money(save)
          + '<span class="bundle-widget__save-info" tabindex="0" role="button" aria-label="How savings are calculated">'
          + SAVE_ICON
          + '<span class="bundle-widget__save-tip" role="tooltip">' + SAVE_TOOLTIP + '</span></span>';
        this.saveEl.style.display = '';
        const info = this.saveEl.querySelector('.bundle-widget__save-info');
        if (info) {
          const closeOnOutside = (ev) => { if (!info.contains(ev.target)) { info.classList.remove('is-open'); document.removeEventListener('click', closeOnOutside); } };
          info.addEventListener('click', (e) => {
            e.stopPropagation();
            if (info.classList.contains('is-open')) {           // open → clicking it again closes
              info.classList.remove('is-open');
              document.removeEventListener('click', closeOnOutside);
            } else {                                             // closed → open + close on any outside click/tap
              info.classList.add('is-open');
              setTimeout(() => document.addEventListener('click', closeOnOutside), 0);
            }
          });
        }
      }
    }

    if (this._initialised) return;
    this._initialised = true;
    // Always start collapsed — the widget only opens when the shopper taps the bar (feedback).
    this.setOpen(false, false);
  }

  toggle() { this.setOpen(this.dataset.open !== 'true', true); }

  setOpen(open, remember) {
    this.dataset.open = open ? 'true' : 'false';
    this.bar.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (remember) {
      try { sessionStorage.setItem(COLLAPSE_KEY, open ? '0' : '1'); } catch (e) {}
    }
  }
}

if (!customElements.get('bundle-widget')) customElements.define('bundle-widget', BundleWidget);
